import { api, ErroreApi } from './api.ts'
import { modoTelefono } from './accesso-ricordato.ts'
import { sistemaTelefono } from './app-nativa.ts'
import { daMostrareQui, perIlSistema, threadAperto, vociNuove, type Novita, type Voce } from './regole-avvisi.ts'

/**
 * Avvisi sullo schermo (richiesta di Davide del 2/11/2026): il riquadro in basso a destra con il
 * gestionale visibile, la notifica del sistema operativo con la scheda in secondo piano.
 *
 * Una sola scheda interroga il server. Le schede aperte dello stesso browser si contendono un lucchetto
 * (Web Locks): chi lo tiene chiede GET /api/avvisi/novita una volta al minuto, mai piu' spesso, e manda
 * il risultato alle altre su un BroadcastChannel. Chiusa quella scheda, il lucchetto passa a un'altra.
 * Un browser senza Web Locks fa girare ogni scheda per conto suo: una richiesta al minuto per scheda, e
 * la notifica del sistema non si ripete grazie al `tag`.
 *
 * Quando si chiede:
 *  - con le notifiche del computer accese (permesso del browser, interruttore di questo computer): sempre,
 *    anche con tutte le schede nascoste, finche' dura la sessione. La rotta non rinnova la sessione
 *    (apps/api/src/plugin/sessione.ts): dopo un'ora senza che la persona tocchi niente il server risponde
 *    401, e alla prima risposta 401 si smette;
 *  - altrimenti come prima di questa versione (useIntervalloAttivo in servizi/sessione.tsx): solo mentre
 *    una scheda e' visibile e qualcuno l'ha toccata nell'ultima mezz'ora. Le altre schede lo dicono a
 *    quella che interroga con un messaggio «presente».
 *
 * Dove si mostra: ogni scheda visibile, con gli avvisi sullo schermo accesi, mostra i riquadri e risponde
 * «mostrati»; se nessuna risponde, la scheda che interroga manda le notifiche del sistema (se accese).
 * Niente riquadro per la conversazione aperta a schermo.
 *
 * Nell'app per il telefono e nella web app dell'iPhone le notifiche del sistema non ci sono: c'e' la push
 * (servizi/app-nativa.ts). Impersonando (524) questo servizio non parte (servizi/sessione.tsx).
 */

export const PERIODO = 60_000
/** Come SILENZIO_MASSIMO di servizi/sessione.tsx (SIC-25). */
const SILENZIO_MASSIMO = 30 * 60_000
/**
 * Con le notifiche del computer si interroga anche a pagina nascosta, ma per un'ora al massimo dall'ultimo
 * tocco, in questa scheda o in un'altra (decisione di Davide del 2/11/2026). Sui computer ricordati la
 * sessione dura 4 ore: il database serverless non deve restare sveglio con lei. Al primo tocco si riparte.
 */
const SISTEMA_MASSIMO = 60 * 60_000
/** Quanto si aspetta la risposta «mostrati» di un'altra scheda prima della notifica del sistema. */
const ATTESA_MOSTRATI = 700
const VISTI_MASSIMI = 400
const EVENTI_DI_INTERAZIONE = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

// --- preferenze di questo computer ------------------------------------------------------------------

export type Preferenze = {
  /** Riquadri in basso a destra con il gestionale visibile. */
  schermo: boolean
  /** Notifiche del sistema con la scheda in secondo piano; servono anche il permesso del browser. */
  sistema: boolean
}

const PREDEFINITE: Preferenze = { schermo: true, sistema: true }
const chiavePreferenze = (utenteId: string) => `cidis-avvisi:${utenteId}`

/**
 * Le preferenze valgono per questo computer e per questa persona: un computer di sede condiviso non
 * passa le scelte di una persona alla successiva. Senza localStorage (navigazione privata, archivio
 * bloccato) valgono le predefinite.
 */
export function leggiPreferenze(utenteId: string): Preferenze {
  try {
    const grezzo = localStorage.getItem(chiavePreferenze(utenteId))
    if (!grezzo) return PREDEFINITE
    const p = JSON.parse(grezzo) as Partial<Preferenze>
    return { schermo: p.schermo !== false, sistema: p.sistema !== false }
  } catch {
    return PREDEFINITE
  }
}

export function salvaPreferenze(utenteId: string, p: Preferenze): void {
  try { localStorage.setItem(chiavePreferenze(utenteId), JSON.stringify(p)) } catch { /* resta per la pagina */ }
  cambiate.forEach((f) => f())
}

const cambiate = new Set<() => void>()
/** Avvisa quando le preferenze cambiano in questa pagina. */
export function seguiPreferenze(f: () => void): () => void {
  cambiate.add(f)
  return () => { cambiate.delete(f) }
}

// --- notifiche del sistema ------------------------------------------------------------------------

export type StatoPermesso = NotificationPermission | 'non_disponibile'

/**
 * Le notifiche del sistema si possono usare qui? Solo nel browser di un computer: nell'app e nella web
 * app del telefono c'e' la push, e i browser dei telefoni non le mostrano senza un service worker.
 */
export function notificheComputerPossibili(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
    && modoTelefono() === null && sistemaTelefono() === 'altro'
}

export function statoPermesso(): StatoPermesso {
  return notificheComputerPossibili() ? Notification.permission : 'non_disponibile'
}

/** Il permesso del browser. Va chiesto da un gesto della persona (un clic), mai all'apertura della pagina. */
export async function chiediPermesso(): Promise<StatoPermesso> {
  if (!notificheComputerPossibili()) return 'non_disponibile'
  try {
    const esito = await Notification.requestPermission()
    cambiate.forEach((f) => f())
    return esito
  } catch {
    return Notification.permission
  }
}

function sistemaAcceso(utenteId: string): boolean {
  return statoPermesso() === 'granted' && leggiPreferenze(utenteId).sistema
}

function notificaDelSistema(v: Voce, vai: (percorso: string) => void): void {
  try {
    // Il `tag` e' l'identificativo del messaggio: la stessa voce da due schede resta una notifica sola.
    const n = new Notification(v.sistema, { tag: v.chiave, lang: 'it' })
    n.onclick = () => {
      window.focus()
      vai(v.dove)
      n.close()
    }
  } catch { /* il browser le rifiuta (per esempio senza service worker): resta la campanella */ }
}

// --- riquadri -------------------------------------------------------------------------------------

type Riquadri = (voci: Voce[], altre: number, soloEmail: boolean) => void
const ascoltatori = new Set<Riquadri>()

/**
 * La conversazione aperta nella pagina Posta, annunciata dalla pagina stessa: ?thread=<id> resta
 * nell'indirizzo solo finche' la conversazione non si apre, e chi la apre a mano dall'elenco non lo ha mai.
 */
let conversazioneAperta: string | null = null
export function annunciaConversazioneAperta(id: string | null): void { conversazioneAperta = id }

/** Il componente dei riquadri (componenti/AvvisiSchermo.tsx) si mette in ascolto qui. */
export function ascoltaRiquadri(f: Riquadri): () => void {
  ascoltatori.add(f)
  return () => { ascoltatori.delete(f) }
}

/**
 * Mostra i riquadri in questa scheda, se e' visibile e gli avvisi sullo schermo sono accesi. Vero se la
 * scheda se n'e' fatta carico, anche quando la sola voce era la conversazione gia' aperta a schermo o un
 * messaggio del messenger con la pagina «Messenger» aperta.
 */
function mostraQui(utenteId: string, voci: Voce[], altre: number, soloEmail: boolean): boolean {
  if (document.hidden || !leggiPreferenze(utenteId).schermo || !ascoltatori.size) return false
  const percorso = window.location.pathname
  const aperto = threadAperto(percorso, window.location.search)
    ?? (percorso === '/posta' ? conversazioneAperta : null)
  const daMostrare = daMostrareQui(voci, percorso, aperto)
  if (daMostrare.length || altre) ascoltatori.forEach((f) => f(daMostrare, altre, soloEmail))
  return true
}

// --- la scheda che interroga ----------------------------------------------------------------------

type Messaggio =
  | { tipo: 'novita'; giro: string; adesso: string; partenza: string; da_leggere: number
      voci: Voce[]; altre: number; soloEmail: boolean }
  | { tipo: 'mostrati'; giro: string }
  | { tipo: 'presente' }

export type OpzioniNovita = {
  utenteId: string
  /** Il conteggio della campanella, a ogni giro. */
  conteggio: (daLeggere: number) => void
  vai: (percorso: string) => void
  /** La sessione e' finita (401): la pagina torna all'accesso invece di restare aperta su dati vecchi. */
  scaduta: () => void
}

/**
 * Avvia gli avvisi per questa scheda e restituisce la funzione che li ferma (uscita, cambio di utente,
 * inizio di un'impersonazione).
 */
export function avviaNovita(o: OpzioniNovita): () => void {
  const canale = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(`cidis-avvisi:${o.utenteId}`)
  const manda = (m: Messaggio) => { try { canale?.postMessage(m) } catch { /* canale chiuso */ } }

  let fermato = false
  let capofila = false
  let rilascia: (() => void) | null = null
  // Il punto a cui si e' arrivati: lo conoscono tutte le schede, perche' ognuna puo' diventare capofila.
  let dal: string | null = null
  let partenza: number | null = null
  const visti = new Set<string>()
  const ricorda = (voci: Voce[]) => {
    for (const v of voci) visti.add(v.chiave)
    while (visti.size > VISTI_MASSIMI) visti.delete(visti.values().next().value!)
  }

  let ultimaInterazione = Date.now()
  let ultimaPresenza = 0
  let ultimoGiro = 0
  let scaduta = false
  let timer: number | null = null
  const attesaMostrati = new Map<string, () => void>()

  const attivaQui = () => !document.hidden && Date.now() - ultimaInterazione < SILENZIO_MASSIMO
  const sistemaAttivo = () => sistemaAcceso(o.utenteId)
    && Date.now() - Math.max(ultimaInterazione, ultimaPresenza) < SISTEMA_MASSIMO
  const deveGirare = () => !fermato && capofila && !scaduta
    && (sistemaAttivo() || attivaQui() || Date.now() - ultimaPresenza < 2 * PERIODO)

  function programma() {
    if (timer !== null || !deveGirare()) return
    // Mai piu' di una richiesta al minuto: la ripresa dopo una pausa rilegge subito solo se il minuto e' passato.
    timer = window.setTimeout(() => { timer = null; void giro() }, Math.max(0, ultimoGiro + PERIODO - Date.now()))
  }

  async function giro() {
    if (!deveGirare()) return
    ultimoGiro = Date.now()
    try {
      const n = await api.get<Novita>(`/avvisi/novita${dal ? `?dal=${encodeURIComponent(dal)}` : ''}`)
      if (fermato) return
      await consegna(n)
    } catch (e) {
      // 401: la sessione e' finita, e con lei gli avvisi. 403: secondo fattore o password da sistemare,
      // ci pensa la pagina. Gli altri errori (rete, server) si riprovano al giro dopo.
      if (e instanceof ErroreApi && (e.stato === 401 || e.stato === 403)) {
        scaduta = true
        if (e.stato === 401) o.scaduta()
        return
      }
    }
    programma()
  }

  async function consegna(n: Novita) {
    const primo = partenza === null
    dal = n.adesso
    if (primo) partenza = Date.parse(n.adesso)
    const voci = primo ? [] : vociNuove(n, visti, partenza!)
    ricorda(voci)
    const altre = primo ? 0 : n.email_altre + n.notifiche_altre
    const soloEmail = n.notifiche_altre === 0
    const giroId = `${n.adesso}:${Math.random().toString(36).slice(2)}`
    o.conteggio(n.da_leggere)
    manda({ tipo: 'novita', giro: giroId, adesso: n.adesso, partenza: new Date(partenza!).toISOString(),
      da_leggere: n.da_leggere, voci, altre, soloEmail })
    if (!voci.length && !altre) return
    if (mostraQui(o.utenteId, voci, altre, soloEmail)) return
    // Nessuna scheda visibile qui: si aspetta un momento la risposta delle altre.
    const mostrata = canale
      ? await new Promise<boolean>((risolvi) => {
        const t = window.setTimeout(() => { attesaMostrati.delete(giroId); risolvi(false) }, ATTESA_MOSTRATI)
        attesaMostrati.set(giroId, () => { window.clearTimeout(t); attesaMostrati.delete(giroId); risolvi(true) })
      })
      : false
    if (mostrata || fermato || !sistemaAcceso(o.utenteId)) return
    for (const v of perIlSistema(voci, altre, soloEmail)) notificaDelSistema(v, o.vai)
  }

  if (canale) {
    canale.onmessage = (e: MessageEvent<Messaggio>) => {
      const m = e.data
      if (fermato || !m) return
      if (m.tipo === 'novita') {
        dal = m.adesso
        partenza = Date.parse(m.partenza)
        ricorda(m.voci)
        o.conteggio(m.da_leggere)
        if ((m.voci.length || m.altre) && mostraQui(o.utenteId, m.voci, m.altre, m.soloEmail)) {
          manda({ tipo: 'mostrati', giro: m.giro })
        }
      } else if (m.tipo === 'mostrati') {
        attesaMostrati.get(m.giro)?.()
      } else if (m.tipo === 'presente') {
        ultimaPresenza = Date.now()
        programma()
      }
    }
  }

  // Le schede che non interrogano dicono a quella che interroga che qualcuno sta guardando.
  let ultimoAnnuncio = 0
  const annuncia = () => {
    if (capofila || !attivaQui() || Date.now() - ultimoAnnuncio < PERIODO / 2) return
    ultimoAnnuncio = Date.now()
    manda({ tipo: 'presente' })
  }
  const annunci = window.setInterval(annuncia, PERIODO)
  // Una scheda appena aperta si annuncia subito, appena si sa se interroga lei: la capofila puo' essere una
  // scheda nascosta, ferma finche' qualcuno non guarda.
  const primoAnnuncio = window.setTimeout(annuncia, 500)

  const alVisibile = () => {
    if (document.hidden) return
    annuncia()
    programma()
  }
  const alTocco = () => {
    ultimaInterazione = Date.now()
    annuncia()
    programma()
  }
  const alleScelte = () => programma()
  document.addEventListener('visibilitychange', alVisibile)
  for (const ev of EVENTI_DI_INTERAZIONE) window.addEventListener(ev, alTocco, { passive: true })
  const smettiScelte = seguiPreferenze(alleScelte)

  const diventaCapofila = () => {
    if (fermato) return
    capofila = true
    programma()
  }
  // Il lucchetto si tiene finche' la scheda resta aperta o finche' gli avvisi non si fermano; una
  // richiesta ancora in coda si ritira con il segnale.
  const ritira = new AbortController()
  if (navigator.locks?.request) {
    navigator.locks.request(`cidis-avvisi:${o.utenteId}`, { signal: ritira.signal }, () => new Promise<void>((r) => {
      rilascia = r
      if (fermato) r()
      else diventaCapofila()
    })).catch(() => undefined)
  } else {
    diventaCapofila()
  }

  return () => {
    fermato = true
    if (timer !== null) window.clearTimeout(timer)
    window.clearInterval(annunci)
    window.clearTimeout(primoAnnuncio)
    document.removeEventListener('visibilitychange', alVisibile)
    for (const ev of EVENTI_DI_INTERAZIONE) window.removeEventListener(ev, alTocco)
    smettiScelte()
    attesaMostrati.forEach((f) => f())
    ritira.abort()
    rilascia?.()
    canale?.close()
  }
}
